import React, { useState } from "react";
import { TrashIcon } from "@heroicons/react/24/solid";
import SearchBar from "../../ui/SearchBar";
import { PageLoadingView } from "../../common/PageLoadingView";
import { PageErrorView } from "../../common/PageErrorView";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import { useUser } from "../../../context/UserInfoProvider";
import { DataManagementSidebar } from "./subcomponents/DataManagementSidebar";
import type { HousekeepingType } from "../../../api/housekeepingApi";
import { useHousekeeping } from "../../../hooks/useHousekeeping";

interface ValidationErrors {
  type?: string;
  description?: string;
  urgency?: string;
}

const HousekeepingData: React.FC = () => {
  const { hotelId } = useUser();

  // Data and CRUD via custom hook
  const {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    refresh,
    setSearchTerm,
  } = useHousekeeping({ hotelId: hotelId || "" });

  // derive total pages from pagination state
  const totalPages = Math.max(
    1,
    Math.ceil(pagination.total / pagination.limit)
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const validateHousekeeping = (
    item: Partial<HousekeepingType>
  ): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!item.type?.trim()) {
      errors.type = "Type name is required";
    }

    if (!item.description?.trim()) {
      errors.description = "Description is required";
    }

    if (item.urgency !== undefined && (item.urgency < 1 || item.urgency > 5)) {
      errors.urgency = "Urgency must be between 1 and 5";
    }

    return errors;
  };

  const handleItemSelect = (item: HousekeepingType) => {
    // If we're creating a new item and user clicks an existing one, warn them
    if (isCreating) {
      setConfirmationDialog({
        isOpen: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes for a new housekeeping type. Do you want to discard these changes and edit the selected type?",
        confirmText: "Discard Changes",
        cancelText: "Cancel",
        onConfirm: () => {
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
          setSelectedItem(item);
          setValidationErrors({});
          setUpdateError(null);
          setIsCreating(false);
        },
      });
      return;
    }

    setSelectedItem(item);
    setValidationErrors({});
    setUpdateError(null);
    setIsCreating(false);
  };

  const handleAddItem = () => {
    setSelectedItem({
      id: "",
      hotelId: hotelId || "",
      type: "",
      description: "",
      urgency: undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setValidationErrors({});
    setUpdateError(null);
    setIsCreating(true);
  };

  const handleSave = async () => {
    if (!selectedItem) return;

    // Validate the form
    const errors = validateHousekeeping(selectedItem);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setValidationErrors({});

      if (isCreating) {
        // Create new housekeeping type
        const itemData = {
          hotelId: hotelId || "",
          type: selectedItem.type,
          description: selectedItem.description,
          urgency: selectedItem.urgency,
        };

        await createItem(
          itemData as Omit<HousekeepingType, "id" | "createdAt" | "updatedAt">
        );

        // Refresh list
        await refresh();
      } else {
        // Update existing housekeeping type
        await updateItem(selectedItem.id, selectedItem);

        await refresh();
      }

      setSelectedItem(null);
      setIsCreating(false);
    } catch (error) {
      console.error("Error saving housekeeping type:", error);
      setUpdateError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while saving the housekeeping type"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete Housekeeping Type",
      message: "Are you sure you want to delete this housekeeping type?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          setIsUpdating(true);
          await deleteItem(itemId);

          // Refresh the list after deleting
          await refresh();
        } catch (error) {
          console.error("Error deleting housekeeping type:", error);
          setUpdateError(
            error instanceof Error
              ? error.message
              : "An unexpected error occurred while deleting the housekeeping type"
          );
        } finally {
          setIsUpdating(false);
        }
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    if ([10, 20, 50].includes(newLimit)) {
      setLimit(newLimit);
    }
  };

  // Items are now filtered server-side using searchTerm in the hook
  const filteredItems = items;

  if (isLoading && !selectedItem) {
    return <PageLoadingView />;
  }

  return (
    <div className="w-full h-screen flex flex-col text-gray-800 dark:text-white overflow-hidden">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Housekeeping Types</h1>
        <button
          onClick={handleAddItem}
          className="bg-blue-600 text-white py-1 px-4 rounded hover:bg-blue-500"
        >
          + Add Type
        </button>
      </div>

      {/* Search Bar */}
      <div className="px-6">
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={(q) => {
            setSearchQuery(q);
            if (q === "") {
              setSearchTerm("");
            }
          }}
          onSearchSubmit={() => {
            setIsSearching(true);
            setSearchTerm(searchQuery);
            setTimeout(() => setIsSearching(false), 100);
          }}
          isLoading={isSearching}
          placeholder="Search for a type"
        />
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-6 no-scrollbar">
        {error ? (
          <PageErrorView
            title="Error loading housekeeping types"
            onRetry={refresh}
          />
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchQuery ? "No types match your search" : "No types found"}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => handleItemSelect(item)}
              >
                <div className="flex-1">
                  <h3 className="text-lg font-medium">{item.type}</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {item.description}
                    </p>
                    {item.urgency && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                        ${
                          item.urgency === 1
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : item.urgency === 2
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : item.urgency === 3
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            : item.urgency === 4
                            ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                        }`}
                      >
                        Urgency: {item.urgency}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.total > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Show
              </span>
              <select
                value={pagination.limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
                className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 dark:text-white pr-8"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                items per page
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800"
              >
                Previous
              </button>
              <span className="text-sm dark:text-gray-400">
                Page {pagination.page} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <DataManagementSidebar
        title={isCreating ? "Add Housekeeping Type" : "Edit Housekeeping Type"}
        selectedItem={selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setIsCreating(false);
          setValidationErrors({});
          setUpdateError(null);
        }}
        onSave={handleSave}
        isUpdating={isUpdating}
        updateError={updateError}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Type Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={selectedItem?.type || ""}
              onChange={(e) =>
                setSelectedItem((prev: HousekeepingType | null) => {
                  if (!prev) return null;
                  const newState: HousekeepingType = {
                    ...prev,
                    type: e.target.value,
                  };
                  return newState;
                })
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.type ? "border-red-500" : ""
              }`}
              placeholder="Enter type name"
            />
            {validationErrors.type && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.type}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={selectedItem?.description || ""}
              onChange={(e) =>
                setSelectedItem((prev: HousekeepingType | null) => {
                  if (!prev) return null;
                  const newState: HousekeepingType = {
                    ...prev,
                    description: e.target.value,
                  };
                  return newState;
                })
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.description ? "border-red-500" : ""
              }`}
              placeholder="Enter description"
              rows={3}
            />
            {validationErrors.description && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.description}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Urgency Level (1-5)
            </label>
            <select
              value={selectedItem?.urgency || ""}
              onChange={(e) =>
                setSelectedItem((prev: HousekeepingType | null) => {
                  if (!prev) return null;
                  const newUrgency = e.target.value
                    ? Number(e.target.value)
                    : undefined;
                  const newState: HousekeepingType = {
                    ...prev,
                    urgency: newUrgency,
                  };
                  return newState;
                })
              }
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                validationErrors.urgency ? "border-red-500" : ""
              }`}
            >
              <option value="">Select urgency level</option>
              <option value="1">1 - Low</option>
              <option value="2">2 - Medium-Low</option>
              <option value="3">3 - Medium</option>
              <option value="4">4 - Medium-High</option>
              <option value="5">5 - High</option>
            </select>
            {validationErrors.urgency && (
              <p className="mt-1 text-sm text-red-500">
                {validationErrors.urgency}
              </p>
            )}
          </div>
        </div>
      </DataManagementSidebar>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={() =>
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
        }
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
      />
    </div>
  );
};

export default HousekeepingData;
